import AppModal from '@/components/AppModal/AppModal';
import BoldSmallText from '@/components/BoldSmallText';
import RegularText from '@/components/RegularText';
import { config } from '@/config';
import { PlanSubscription, PricingPlan } from '@/constants/enum';
import { PATH } from '@/constants/path';
import { arrayToStringByComma, openCrisp } from '@/helpers';
import { useRedirect } from '@/hooks/useRedirect';
import { useGetGeneralSettingQuery, useLazyGetChargeUrlQuery } from '@/redux/query';
import { Banner, BlockStack, Box, Button, Checkbox, InlineStack, Link, List } from '@shopify/polaris';

import { Dispatch, SetStateAction, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';

interface IProps {
  open: boolean;
  currentPlan: string;
  chosenPlan: PricingPlan;
  chosenSubscription: PlanSubscription;
  setIsOpenDownrageModal: Dispatch<SetStateAction<boolean>>;
  setIsOpenLocationModal: Dispatch<SetStateAction<boolean>>;
  locationList?: {
    value: number;
    label: string;
  }[];
  locationIds: number[];
  setLocationIds: Dispatch<SetStateAction<number[]>>;
  refetchLocation: () => void;
  isFetchingLocation: boolean;
}

export const DownrageModal = ({
  open,
  chosenPlan,
  chosenSubscription,
  setIsOpenDownrageModal,
  setIsOpenLocationModal,
  locationList,
  locationIds,
  setLocationIds,
  refetchLocation,
  isFetchingLocation,
}: IProps) => {
  const [getChargeUrl, { isFetching: isFetchingChargeUrl }] = useLazyGetChargeUrlQuery();
  const { refetch: refetchGeneralSetting } = useGetGeneralSettingQuery({ shop: config.shop });
  const [isConfirmDowrgrade, setIsConfirmDowngrade] = useState(false);
  const { redirectTo } = useRedirect();
  const navigate = useNavigate();

  const freePlan = useMemo(
    () => ['1 location', '20 scheduled orders/ month', 'No specific product-based rules', 'and many advanced features'],
    [],
  );

  const essentialPlan = useMemo(
    () => ['2 locations', '100 scheduled orders/ month', 'No customized design', 'and many advanced features'],
    [],
  );

  const smartPlan = useMemo(
    () => ['5 locations', '2500 scheduled orders/ month', 'No VIP live chart', 'and many advanced features'],
    [],
  );

  const planDetails = useMemo(() => {
    switch (chosenPlan) {
      case PricingPlan.Free:
        return freePlan;
      case PricingPlan.Essential:
        return essentialPlan;
      case PricingPlan.Smart:
        return smartPlan;
      default:
        return [];
    }
  }, [chosenPlan, essentialPlan, freePlan, smartPlan]);

  const planName = useMemo(() => {
    switch (chosenPlan) {
      case PricingPlan.Free:
        return 'Free Plan';
      case PricingPlan.Essential:
        return 'Essential Plan';
      case PricingPlan.Smart:
        return 'Smart Plan';
      default:
        return [];
    }
  }, [chosenPlan]);

  const getLocationQuota = (plan: string) => {
    switch (plan) {
      case PricingPlan.Free:
        return 1;
      case PricingPlan.Essential:
        return 2;
      case PricingPlan.Smart:
        return 5;
      default:
        return 20;
    }
  };

  const renderWarningBanner = (isOverLocations: boolean) => {
    if (isOverLocations) {
      return (
        <Banner tone="critical">
          <BlockStack gap={'200'}>
            <Box>
              <BoldSmallText>Important:</BoldSmallText> Your current number of locations are over the quota of {planName}, please
              choose location you want to keep the settings for
            </Box>
            <InlineStack>
              <Button
                onClick={() => {
                  setIsOpenDownrageModal(false);
                  setIsOpenLocationModal(true);
                }}
              >
                Choose location
              </Button>
            </InlineStack>
          </BlockStack>
        </Banner>
      );
    } else {
      return (
        <Banner tone="critical">
          <BlockStack gap={'200'}>
            <Box>
              <BoldSmallText as="span">Important:</BoldSmallText> Your existing data, if any, that are{' '}
              <BoldSmallText as="span">beyond limit of {planName} will be permanently deleted</BoldSmallText> and cannot be
              recovered. Please review your current settings before taking further actions.
            </Box>
            <InlineStack>
              <Button onClick={() => navigate(PATH.DEFAULT)}>Review settings</Button>
            </InlineStack>
          </BlockStack>
        </Banner>
      );
    }
  };

  const isOverQuotaLocation = useMemo(() => {
    return getLocationQuota(chosenPlan) < (locationList?.length || 0);
  }, [chosenPlan, locationList?.length]);

  const handleCharge = () => {
    getChargeUrl({
      shop: config.shop,
      plan: chosenPlan,
      subscription: chosenSubscription,
      locationIds: isOverQuotaLocation
        ? arrayToStringByComma(locationList?.map((item) => item.value).filter((id) => !locationIds.includes(id)) ?? [])
        : '',
    })
      .unwrap()
      .then((data) => {
        Promise.all([refetchLocation(), refetchGeneralSetting()]);
        if (!isFetchingLocation) {
          setIsOpenDownrageModal(false);
        }
        if (chosenPlan !== PricingPlan.Free) {
          redirectTo(data.data.url);
        } else window.location.reload();
      })
      .catch(() => {});
  };

  return (
    <>
      <AppModal
        title="Review plan downgrade and data storage policy"
        open={open}
        onCancel={() => {
          setLocationIds([]);
          setIsOpenDownrageModal(false);
        }}
        onSubmit={handleCharge}
        submitText="Stay on current plan"
        cancelText="Confirm downgrade"
        swapActions
        disabledCancel={!isConfirmDowrgrade}
        loadingCancel={isFetchingChargeUrl || isFetchingLocation}
      >
        <BlockStack gap={'100'}>
          <RegularText>
            Downgrading to <BoldSmallText as="span">{planName}</BoldSmallText> will limit you to:
          </RegularText>
          <List>
            {planDetails.map((item, index) => (
              <List.Item key={index}>{item}</List.Item>
            ))}
          </List>
        </BlockStack>
        {renderWarningBanner(isOverQuotaLocation)}
        <Box paddingBlockStart={'200'} paddingBlockEnd={'400'}>
          <RegularText>
            Please{' '}
            <Link removeUnderline onClick={openCrisp}>
              contact support
            </Link>{' '}
            if you have additional questions
          </RegularText>
        </Box>
        <Checkbox
          label={
            <RegularText>
              I understand that any data beyond limit of new plan will be permanently deleted and cannot be recovered.
              <RegularText tone="critical">*</RegularText>
            </RegularText>
          }
          checked={isConfirmDowrgrade}
          onChange={() => setIsConfirmDowngrade(!isConfirmDowrgrade)}
          disabled={isOverQuotaLocation && !locationIds.length}
        />
      </AppModal>
    </>
  );
};
